<template>
  <div v-on:touchmove="iOSpreventGlobalTouchMove" v-on:touchend="iOSpreventGlobalTouchMoveEnd">
    <div v-if="captcha_required" class="overlay">
      <div class="card-body">
        <checkbox-captcha v-on:completed="completeCaptcha($event)" v-bind="captchaConfig || {}" style="display: flex; justify-content: center" />
      </div>
    </div>

    <div v-if="loadingStatus == 'loading'" class="overlay">
      <img src="/images/loader.gif" alt="Processing…" style="width: 25px; height: 25px; display: inline-block; margin: 10px" />
      {{ I18n.t("loading_image") }}
    </div>
    <div v-if="loadingStatus == 'error'" class="overlay">
      <div class="alert alert-danger">
        <p>
          <strong>{{ I18n.t("failed_to_open_image") }}</strong>
        </p>

        <p>
          <small>{{ this.loadingErrorMessage }}</small>
        </p>

        <p>
          {{ I18n.t("please_try_again_or_contact_us") }}
        </p>
      </div>
    </div>
    <download-overlay
      v-bind:url="downloadUrlResult ? downloadUrlResult.url : null"
      v-bind:blob="downloadUrlResult ? downloadUrlResult.blob : null"
      v-bind:filename="downloadFilename"
      v-bind:locale="locale"
      v-if="downloading"
      v-on:close="
        downloading = false;
        expireDownload();
      "
      v-on:downloaded="warnBeforeClose = false"
    />
    <div v-bind:class="{ 'editor-layout': true, 'editor-layout-has-control-area-opened': controlTabsOpened }">
      <div class="editor-area">
        <div class="editor-upper-controls">
          <button :title="I18n.t('modal_close')" class="editor-button close-button" v-on:click="close">
            <i class="fas fa-times"></i>
          </button>
          <div class="editor-button-group d-none d-sm-block" v-if="!downloading">
            <button :title="I18n.t('zoom_out')" class="editor-button zoom-out-button" v-on:click="zoomOut" v-bind:disabled="!canZoomOut || uiDisabled">
              <i class="fal fa-minus"></i>
            </button>
            <button :title="I18n.t('set_zoom_to_100_percent')" class="editor-button zoom-restore-button" v-on:click="zoomReset">{{ Math.round(zoom * 100) }}%</button>
            <button :title="I18n.t('zoom_in')" class="editor-button zoom-in-button" v-on:click="zoomIn" v-bind:disabled="!canZoomIn || uiDisabled">
              <i class="fal fa-plus"></i>
            </button>
          </div>
          <div class="editor-button-group" style="min-width: 75px">
            <button v-show="undoRedoVisible" :title="I18n.t('undo')" class="editor-button undo-button" v-on:click="undo" v-bind:disabled="!canUndo" :disabled="uiDisabled">
              <i class="fal fa-undo-alt"></i>
            </button>
            <button v-show="undoRedoVisible" :title="I18n.t('redo')" class="editor-button redo-button" v-on:click="redo" v-bind:disabled="!canRedo" :disabled="uiDisabled">
              <i class="fal fa-redo-alt"></i>
            </button>
          </div>
          <div v-if="signed_in && detectObjectsAvailable && aiBrushFormData && hdAvailable && hdEnabled" class="prism d-sm-none">
            <Dropdown align-right>
              <template v-slot:trigger>
                <button :title="I18n.t('download')" class="editor-button download-button d-sm-none" :disabled="uiDisabled">
                  <i class="fas fa-download"></i>
                </button>
              </template>
              <template v-slot:content class="!mr-2">
                <div class="!p-4 flex flex-col gap-y-4" style="width: 200px">
                  <span @click.prevent="download">{{ I18n.t("image.download") }}</span>
                  <span @click.prevent="downloadHD">{{ I18n.t("image.download_high_res") }}</span>
                </div>
              </template>
            </Dropdown>
          </div>
          <button v-else :title="I18n.t('download')" @click="download" class="editor-button download-button d-sm-none" :disabled="uiDisabled">
            <i class="fas fa-download"></i>
          </button>
        </div>
        <div v-bind:class="{ 'editor-area-content': true, 'tool-active': tool != null, 'pointer-events-none': uiDisabled }" v-on:mousemove="updateCursorPosition" v-on:touchstart.passive="touchStarted" v-on:touchmove="onTouchMove" ref="editorArea">
          <div class="editor-area-content-inner" v-bind:class="{ 'editor-area-content-inner-background-image': !detectObjectsEnabled || brushMode == 'erase' }" ref="editorCanvas" v-on:contextmenu="$event.preventDefault()" v-bind:data-ios-emulate-scroll="tool == 'erase_restore' ? false : 'xy'" data-hj-suppress>
            <div id="base" ref="base" style="position: relative">
              <div class="position-absolute overflow-hidden" style="z-index: 9" v-if="detectObjectsAvailable">
                <div class="prism">
                  <MagicOverlay v-show="aiBrushProcessing || aiBrushFullProcessing"></MagicOverlay>
                </div>
                <!-- magic mask -->
                <canvas id="mask" ref="mask" style="cursor: none; opacity: 0.5 !important"></canvas>
              </div>

              <img ref="restoreBackground" v-bind:src="originalUrl" alt="input with opacity" class="position-absolute" style="opacity: 0.25; max-width: unset" :style="{ display: detectObjectsEnabled && brushMode == 'restore' && originalUrl ? 'inline' : 'none' }" />
            </div>
          </div>

          <div
            v-bind:class="{ 'editor-cursor': true, 'editor-cursor-erase': brushMode === 'erase', 'editor-cursor-restore': brushMode === 'restore', touchdown: isTouchDown }"
            v-bind:style="{
              left: `${cursorPosition[0]}px`,
              top: `${cursorPosition[1] - brushOffset}px`,
              'margin-left': `-${(this.zoom * brushSize) / 2}px`,
              'margin-top': `-${(this.zoom * brushSize) / 2}px`,
              width: `${this.zoom * brushSize + 2}px`,
              height: `${this.zoom * brushSize + 2}px`,
            }"
            v-on:mousemove="updateCursorPosition"
          >
            <div
              class="editor-finger-cursor"
              v-if="touchDetected"
              v-bind:style="{
                top: `${(this.zoom * brushSize) / 2 + brushOffset}px`,
              }"
            >
              <i class="fal fa-hand-pointer"></i>
            </div>
          </div>
        </div>
        <div ref="lowercontrols" class="editor-lower-controls d-none d-sm-flex align-items-center justify-content-center prism" style="z-index: 10">
          <Button :title="I18n.t('download')" @click.prevent="download" :disabled="uiDisabled">{{ I18n.t("image.download") }}</Button>
          <div v-if="detectObjectsAvailable && aiBrushFormData && hdAvailable && hdEnabled" class="ml-2">
            <div v-if="signed_in">
              <Button variant="secondary" @click.prevent="downloadHD" :loading="aiBrushFullProcessing" :disabled="uiDisabled">{{ I18n.t("image.download_high_res") }}</Button>
            </div>
            <div v-else>
              <Button variant="secondary" :disabled="true">{{ I18n.t("image.download_high_res") }}</Button>
              <span class="hover-tooltip" style="margin-left: 5px; opacity: 0.8" :data-tippy-content="I18n.t('download_hd_signed_out_popup')" tabindex="0">
                <i class="fas fa-info-circle" style="font-size: 75%"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
      <div class="control-area">
        <div class="control-tab-buttons">
          <button v-bind:class="{ 'tab-button': true, 'tab-button-active': controlTab == 'background' && controlTabsOpened }" v-on:click="showTab('background')" :disabled="uiDisabled">
            <i v-bind:class="{ fas: controlTab == 'background' && controlTabsOpened, fal: !(controlTab == 'background' && controlTabsOpened), 'fa-layer-group': true }"></i>
            {{ I18n.t("tab_background") }}
          </button>
          <button v-bind:class="{ 'tab-button': true, 'tab-button-active': controlTab == 'erase_restore' && controlTabsOpened }" v-on:click="showTab('erase_restore')" v-on:touchstart="touchDetected = true" :disabled="uiDisabled">
            <i v-bind:class="{ fas: controlTab == 'erase_restore' && controlTabsOpened, fal: !(controlTab == 'erase_restore' && controlTabsOpened), 'fa-paint-brush': true }"></i>
            {{ I18n.t("tab_erase_restore") }}
          </button>
          <button v-bind:class="{ 'tab-button': true, 'd-sm-none': true }" v-on:click="showTab(null)" v-if="controlTabsOpened" :disabled="uiDisabled">
            <i class="fal fa-chevron-down"></i>
            {{ I18n.t("tab_close") }}
          </button>
          <button v-bind:class="{ 'tab-button': true, 'd-sm-none': true }" v-on:click="showTab(controlTab)" v-if="!controlTabsOpened" :disabled="uiDisabled">
            <i class="fal fa-chevron-up"></i>
            {{ I18n.t("tab_open") }}
          </button>
        </div>
        <div class="control-tabs" data-ios-emulate-scroll="y">
          <div class="control-tab" v-if="controlTab == 'background'">
            <div class="background-tab-content">
              <div style="font-size: 70%">
                {{ I18n.t("blur") }}
              </div>
              <blur-options v-bind:value="background.blur ? background.blur : 0" v-bind:foregroundUrl="foregroundThumbnail" v-bind:backgroundUrl="background.type == 'image' ? background.image : originalUrl" v-on:change="change({ background: { type: 'image', image: background.type == 'image' ? background.image : originalUrl, blur: $event.newValue } })" />

              <div style="margin: 10px auto; text-align: center" class="background-type-toggle">
                <toggle v-bind:initial-value="backgroundTypeTab" left-value="photo" :left-label="I18n.t('photo')" right-value="color" :right-label="I18n.t('color')" v-on:change="backgroundTypeTab = $event.newValue" />
              </div>

              <div v-bind:class="{ 'background-type-tab': true, 'background-type-tab-active': backgroundTypeTab == 'photo' }">
                <div style="font-size: 70%">
                  {{ I18n.t("photo") }}
                </div>
                <image-list v-bind:foregroundType="foregroundType" v-bind:value="background.type == 'image' ? background.image : null" v-bind:foregroundUrl="foregroundThumbnail" v-bind:locale="locale" v-on:change="change({ background: { type: 'image', image: $event.newValue, blur: background.blur } })" v-on:openSearchBackgroundOverlay="openSearchBackgroundOverlay" v-on:setPixabayImages="setPixabayImages" />
              </div>

              <div v-bind:class="{ 'background-type-tab': true, 'background-type-tab-active': backgroundTypeTab == 'color' }">
                <div style="font-size: 70%">
                  {{ I18n.t("color") }}
                </div>
                <color-list v-bind:value="background.type == 'color' ? background.color : null" v-bind:foregroundUrl="foregroundThumbnail" v-bind:transparent="background.type == 'transparent' ? true : false" v-on:change="change($event.newValue ? { background: { type: 'color', color: $event.newValue } } : { background: { type: 'transparent' } })" />
              </div>
            </div>
          </div>
          <div class="control-tab" v-if="controlTab == 'erase_restore'">
            <div v-if="detectObjectsAvailable" class="my-2 my-md-4">
              <div id="explainer" class="d-flex align-items-center justify-content-center rounded-lg py-2 mx-auto position-relative" style="max-width: 280px; min-height: 120px">
                <div class="position-absolute d-flex" style="top: -9px; right: 0px">
                  <span class="badge badge-pill badge-warning mr-2" style="background: #ffc83e">{{ I18n.t("ai_brush.beta") }}</span>
                  <a href="/help/a/how-to-use-magic-brush" class="badge badge-pill badge-light" style="color: #454545 !important" target="_blank">?</a>
                </div>
                <div v-if="detectObjectsEnabled" class="d-flex align-items-center justify-content-center px-2">
                  <transition name="fade" mode="out-in">
                    <img v-if="brushMode == 'erase'" src="/images/erase.gif" class="rounded-lg" width="100" height="100" />
                    <img v-else src="/images/restore.gif" class="rounded-lg" width="100" height="100" />
                  </transition>
                  <small class="pl-2">
                    <span>{{ I18n.t("ai_brush.explainer_text") }}</span>
                    <div class="pt-2">
                      <a href="#" class="text-decoration-underline" @click.prevent="detectObjectsEnabled = shouldEnableDetectObjects(false)">{{ I18n.t("ai_brush.turn_off") }}</a>
                    </div>
                  </small>
                </div>
                <div v-else>
                  <small class="d-flex flex-column align-items-center justify-content-center text-center">
                    <p class="m-0 mb-1">{{ I18n.t("ai_brush.turned_off") }}</p>
                    <a href="#" class="text-decoration-underline" @click.prevent="detectObjectsEnabled = shouldEnableDetectObjects(true)">{{ I18n.t("ai_brush.turn_on") }}</a>
                  </small>
                </div>
              </div>
            </div>
            <div v-else class="my-2 my-md-4">
              <div id="explainer" class="d-flex flex-column justify-content-center rounded-lg py-2 mx-auto px-4" style="max-width: 280px; min-height: 120px">
                <div>
                  <span class="badge badge-pill badge-warning" style="background: #ffc83e">{{ I18n.t("ai_brush.coming_soon") }}</span>
                </div>
                <small class="pt-2">
                  <span class="font-weight-bold">{{ I18n.t("ai_brush.coming_soon_explainer") }}</span>
                  <div class="pt-2">
                    <a href="/help/a/how-to-use-magic-brush" class="text-decoration-underline" target="_blank">{{ I18n.t("ai_brush.learn_more") }}</a>
                  </div>
                </small>
              </div>
            </div>
            <div class="mx-auto my-4 text-center d-flex justify-content-center align-items-center">
              <div class="d-flex justify-content-center align-items-center">
                <div class="content-center mr-3">
                  <button
                    :class="{
                      'bg-button-active': brushMode == 'erase',
                      'bg-button-inactive': brushMode != 'erase',
                      'border-primary': brushMode == 'erase',
                    }"
                    class="border rounded flex-grow px-3 py-2 px-md-4 py-md-4 text-black font-medium d-flex align-items-center justify-content-center"
                    @click="
                      editor.setMode('erase');
                      brushMode = 'erase';
                    "
                    :disabled="uiDisabled"
                  >
                    <i class="fas fa-minus-circle fa-lg"></i>
                    <span :class="{ 'font-weight-bold': brushMode == 'erase' }" class="d-md-none ml-2">{{ I18n.t("erase") }}</span>
                  </button>
                  <p class="text-center m-0 d-none d-md-block" :class="{ 'font-weight-bold': brushMode == 'erase' }">{{ I18n.t("erase") }}</p>
                </div>
                <div>
                  <button
                    :class="{
                      'bg-button-active': brushMode == 'restore',
                      'bg-button-inactive': brushMode != 'restore',
                      'border-primary': brushMode == 'restore',
                    }"
                    class="border rounded flex-grow px-3 py-2 px-md-4 py-md-4 text-black font-medium d-flex align-items-center justify-content-center"
                    @click="
                      editor.setMode('restore');
                      brushMode = 'restore';
                    "
                    :disabled="uiDisabled"
                  >
                    <i class="fas fa-plus-circle fa-lg"></i>
                    <span :class="{ 'font-weight-bold': brushMode == 'restore' }" class="d-md-none ml-2">{{ I18n.t("restore") }}</span>
                  </button>
                  <p class="text-center m-0 d-none d-md-block" :class="{ 'font-weight-bold': brushMode == 'restore' }">{{ I18n.t("restore") }}</p>
                </div>
              </div>
            </div>
            <div class="my-2 my-md-4 mx-auto" style="max-width: 280px">
              <small>
                {{ I18n.t("brush_size") }}
              </small>
              <slider
                v-bind:initial-value="brushSize"
                v-bind:min-value="4"
                v-bind:max-value="brushSizeMax"
                v-on:change="
                  editor.setBrushSize($event.newValue);
                  brushSize = $event.newValue;
                "
                v-on:drag="isTouchDown = true"
                v-on:drop="isTouchDown = false"
                v-bind:class="{ 'pointer-events-none': uiDisabled }"
              />
              <div v-if="touchDetected && (!detectObjectsAvailable || !detectObjectsEnabled)">
                <div style="font-size: 70%">
                  {{ I18n.t("offset") }}
                </div>
                <offset-slider
                  v-bind:initial-value="brushOffset"
                  v-bind:min-value="-brushSizeMax"
                  v-bind:max-value="brushSizeMax"
                  v-on:change="
                    editor.setBrushOffset($event.newValue);
                    brushOffset = $event.newValue;
                  "
                  v-on:drag="isTouchDown = true"
                  v-on:drop="isTouchDown = false"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <search-background-overlay v-bind:foregroundUrl="foregroundUrl" v-on:change="change({ background: { type: 'image', image: $event.newValue, blur: background.blur } })" v-on:close="searchBackgroundOverlayOpen = false" v-bind:pixabayImages="pixabayImages" v-if="searchBackgroundOverlayOpen" />
    </div>
  </div>
</template>

<script>
import EditorUI from "../src/editor/editor_ui";
import Editor from "../src/editor/editor";
import Toggle from "./toggle";
import Slider from "./slider";
import OffsetSlider from "./offset_slider";
import ColorList from "./color_list";
import ImageList from "./image_list";
import BlurOptions from "./blur_options";
import DownloadOverlay from "./download_overlay";
import SearchBackgroundOverlay from "./search_background_overlay";
import { ToggleButton } from "vue-js-toggle-button";
import { fabric } from "fabric";
import { ZipCanvas } from "../src/remove-bg-tools";
import { Base64 } from "js-base64";
import { hcaptcha_invisible } from "../src/hcaptcha";
import tippy from "tippy.js";
import CheckboxCaptcha from "./checkbox_captcha";

import { MagicOverlay, Button, Dropdown, DropdownBox, NavLink } from "prism";

import isiOSSafari from "../src/is_ios_safari";

export default {
  props: {
    originalUrl: String,
    foregroundUrl: String,
    filename: String,
    foregroundType: String,
    locale: String,
    detect_objects_enabled: Boolean,
    resultId: String,
    signed_in: Boolean,
    t: Object,
  },
  data() {
    return {
      controlTabsOpened: true,
      controlTab: "background",
      loadingStatus: "loading",
      loadingErrorMessage: null,
      backgroundTypeTab: "photo",
      editor: null,
      brushMode: "erase",
      brushSize: 120,
      brushSizeMax: 200,
      brushOffset: 0,
      background: {
        type: "transparent",
      },
      history: [],
      historyIndex: -1,
      foregroundThumbnail: null,
      foregroundThumbnailNeedsUpdate: false,
      zoom: 1.0,
      zoomMin: 0.2,
      zoomMax: 4.0,
      cursorPosition: [0, 0],
      isTouchDown: false,
      touchDetected: false,
      pinchPositions: null,
      downloading: false,
      downloadUrlResult: null,
      iosScrollingX: null,
      iosScrollingY: null,
      iosScrollingXPrevious: null,
      iosScrollingYPrevious: null,
      searchBackgroundOverlayOpen: false,
      pixabayImages: null,
      warnBeforeClose: false,
      switchLabels: {
        checked: "On",
        unchecked: "Off",
      },
      switchColors: {
        checked: "#454545",
        disabled: "#8CE4BF",
        unchecked: "#454545",
      },
      detectObjectsEnabled: null,
      canvas: null,
      maskBlob: null,
      resultAlphaBlob: null,
      warnBeforeDetectObjectsEnabled: false,
      aiBrushProcessing: false,
      aiBrushFullProcessing: false,
      aiBrushNextPollIn: 0,
      aiBrushFullNextPollIn: 0,
      aiBrushTimeout: null,
      aiBrushRemote: null,
      aiBrushEraseColor: "#FF4757",
      aiBrushRestoreColor: "#489D4A",
      aiBrushFormData: null,
      maskTimeout: null,
      explainerImage: "/images/erase.gif",
      undoRedoVisible: true,
      downloadFilename: null,
      hdAvailable: false,
      hdEnabled: false,
      captcha_required: false,
      captchaConfig: null,
    };
  },
  mounted: function () {
    var vm = this;
    vm.editor = new Editor({
      target: vm.$refs.editorCanvas,
      original_url: vm.originalUrl,
      foreground_url: vm.foregroundUrl,
    });

    this.renderEditor(() => {
      if (this.detectObjectsAvailable) {
        this.createMagicCanvas();
      }

      this.resizeRestoreBackground();

      window.track("Images", "open_editor", "Open Editor");

      vm.editor.on("change", () => {
        vm.warnBeforeDetectObjectsEnabled = true;
      });

      vm.editor.on("pathStopped", () => {
        vm.change("editor");
        vm.foregroundChanged();
        if (vm.editor.mode == "erase") {
          window.track("Editor", "pixels_erased", "Pixels erased");
        } else {
          window.track("Editor", "pixels_restored", "Pixels restored");
        }
      });
    });
  },
  computed: {
    uiDisabled: function () {
      return this.aiBrushProcessing || this.aiBrushFullProcessing;
    },
    detectObjectsAvailable: function () {
      if (this.foregroundType == "car") {
        return false;
      }
      if (this.editor && this.editor.width * this.editor.height > 250000) {
        return false;
      }
      if (!this.detect_objects_enabled) {
        return false;
      }
      return true;
    },
    canUndo: function () {
      return this.historyIndex >= 0;
    },
    canRedo: function () {
      return this.historyIndex < this.history.length - 1;
    },
    canZoomIn() {
      return this.zoom < this.zoomMax - 0.01;
    },
    canZoomOut() {
      return this.zoom > this.zoomMin + 0.01;
    },
    tool() {
      if (this.controlTabsOpened && this.controlTab == "erase_restore") {
        return "erase_restore";
      }
      return null;
    },
  },
  watch: {
    hdAvailable: function () {
      // need to wait for this to become true otherwise the element is not in the dom
      this.updateTooltips();
    },
    background: function (newValue, oldValue) {
      if (newValue.type == "color") {
        this.editor.setBackground({ color: newValue.color });
      } else if (newValue.type == "image") {
        if (newValue.type != oldValue.type || newValue.image != oldValue.image) {
          this.editor.setBackground({ image_url: newValue.image });
        }
      } else if (newValue.type == "transparent") {
        this.editor.setBackground(false);
      }
      if (newValue.blur != oldValue.blur) {
        if (newValue.blur) {
          this.editor.setBackgroundBlur(newValue.blur);
        } else {
          this.editor.setBackgroundBlur(0);
        }
      }
    },
    zoom: function (newValue, oldValue) {
      var canvas = this.$refs.editorCanvas;
      var posX = (canvas.scrollLeft + canvas.clientWidth / 2) / canvas.scrollWidth;
      var posY = (canvas.scrollTop + canvas.clientHeight / 2) / canvas.scrollHeight;

      var zoomedDimensions = this.editor.setZoom(newValue); // only the editor knows how big it is

      canvas.scrollLeft = canvas.scrollWidth * posX - canvas.clientWidth / 2;
      canvas.scrollTop = canvas.scrollHeight * posY - canvas.clientHeight / 2;

      this.resizeRestoreBackground(newValue);

      // set css dimensions for AI brushes (don't change the actual canvas resolution)
      this.resizeMask(zoomedDimensions[0] + "px", zoomedDimensions[1] + "px", { cssOnly: true });
    },
    controlTab: function (newValue, oldValue) {
      if (this.detectObjectsAvailable && this.detectObjectsEnabled && this.editor) {
        if (newValue == "erase_restore") {
          this.canvas.isDrawingMode = true;
        } else {
          this.canvas.isDrawingMode = false;
        }
      }

      if (newValue == "background" && this.foregroundThumbnailNeedsUpdate) {
        this.updateForegroundThumbnail();
      }
    },
    maskBlob() {
      // this.process();
    },
    brushSize() {
      if (this.canvas && this.canvas.freeDrawingBrush) this.canvas.freeDrawingBrush.width = this.brushSize;
    },
    brushMode(mode) {
      if (this.detectObjectsAvailable) {
        if (mode == "erase") {
          this.canvas.freeDrawingBrush.color = this.aiBrushEraseColor;
          this.explainerImage = "/images/erase.gif";
        } else {
          this.canvas.freeDrawingBrush.color = this.aiBrushRestoreColor;
          this.explainerImage = "/images/restore.gif";
        }
      }
    },
  },
  methods: {
    completeCaptcha(value) {
      this.onCaptchaCompleted({
        value: value,
        version: "v2_checkbox",
      });
    },
    updateTooltips() {
      let that = this;
      this.$nextTick(() => {
        var tooltips = $(that.$refs.lowercontrols).find(".hover-tooltip");
        if (tooltips.length > 0) {
          tippy(tooltips.get(), { zIndex: 999999 });
        }
      });
    },
    // we need this to prevent switching to AI brushes if user says no
    shouldEnableDetectObjects: function (enabled) {
      if (this.uiDisabled) return !enabled;
      this.brushOffset = 0;
      if (enabled && this.warnBeforeDetectObjectsEnabled) {
        if (window.confirm(I18n.t("ai_brush.turn_on_warning"))) {
          this.editor.removeSvg();
          this.renderEditor(() => {
            this.warnBeforeDetectObjectsEnabled = false;
          }); // reset editor brushes
        } else {
          return false; // disable switching
        }
      }

      if (enabled) {
        this.showMagicCanvas();
      } else {
        this.hideMagicCanvas();
      }
      this.editor.setDetectObjectsEnabled(enabled);
      return enabled;
    },
    renderEditor: function (cb) {
      let vm = this;
      vm.editor
        .render()
        .then(() => {
          vm.zoom = 1.0;
          vm.detectObjectsEnabled = this.detectObjectsAvailable;
          vm.loadingStatus = "loaded";
          vm.foregroundThumbnail = vm.foregroundUrl;
          vm.$emit("loaded", {
            width: vm.editor.width + 500,
            height: vm.editor.height + 130,
          });
          var maxDimension = vm.editor.width > vm.editor.height ? vm.editor.width : vm.editor.height;
          // vm.brushSize = maxDimension * 0.05;
          vm.editor.setBrushSize(vm.brushSize);
          var brushSizeMax = maxDimension * 0.1;
          if (brushSizeMax > vm.brushSizeMax) vm.brushSizeMax = brushSizeMax;

          var rect = this.$refs.editorArea.getBoundingClientRect();
          vm.cursorPosition = [rect.left + (rect.right - rect.left) / 2, rect.top + (rect.bottom - rect.top) / 2];

          if (cb) cb();
        })
        .catch((e) => {
          vm.loadingStatus = "error";
          vm.loadingErrorMessage = e;
          console.error(e);
          if (window.error_tracking) window.error_tracking.sendError(e);
        });
    },
    showTab: function (tab) {
      if (tab == null) {
        this.controlTabsOpened = false;
        this.editor.setMode(null);
        this.editor.setDetectObjectsEnabled(null);
        this.canvas.isDrawingMode = false;
      } else {
        this.controlTab = tab;
        this.controlTabsOpened = true;
        this.brushMode = "erase";
        if (tab == "erase_restore") {
          this.editor.setMode(this.brushMode);
          this.editor.setDetectObjectsEnabled(this.detectObjectsEnabled);
          if (this.detectObjectsEnabled) {
            this.canvas.isDrawingMode = true;
          }
        } else {
          this.editor.setMode(null);
          this.editor.setDetectObjectsEnabled(null);
        }
      }
    },
    change(diff) {
      this.warnBeforeClose = true;
      if (this.canRedo) {
        this.history.splice(this.historyIndex + 1, this.history.length);
        this.editor.clearRedoStack();
      }
      var undo, redo;
      if (diff == "editor") {
        undo = { editor: "undo" };
        redo = { editor: "redo" };
      } else {
        undo = {};
        for (var key in diff) {
          undo[key] = this[key];
        }
        redo = diff;
      }
      this.history.push({ undo, redo });
      this.historyIndex++;
      if (diff != "editor") {
        this.applyChange(diff);
      }
    },
    applyChange(diff) {
      if ("editor" in diff) {
        this.editor[diff.editor]();
        this.foregroundChanged();
      } else if ("foreground" in diff) {
        this.foregroundChanged(diff.foreground.image);
      } else {
        for (var key in diff) {
          this[key] = diff[key];
        }
      }
    },
    undo() {
      if (!this.canUndo) return;
      this.applyChange(this.history[this.historyIndex].undo);
      this.historyIndex--;
      window.track("Editor", "undo", "Undo");
    },
    redo() {
      if (!this.canRedo) return;
      this.applyChange(this.history[this.historyIndex + 1].redo);
      this.historyIndex++;
      window.track("Editor", "redo", "Redo");
    },
    close() {
      if (!this.warnBeforeClose || window.confirm(I18n.t("warn_before_closing"))) {
        EditorUI.hide();
      }
    },
    foregroundChanged(blob = null) {
      if (this.controlTab == "background" || (this.controlTab == "erase_restore" && blob)) {
        this.updateForegroundThumbnail(blob);
      } else {
        this.foregroundThumbnailNeedsUpdate = true;
      }
    },
    updateForegroundThumbnail(blob = null) {
      this.foregroundThumbnailNeedsUpdate = false;
      this.editor.toUrl({ hideBackground: true, thumbnail: true, blob: blob }).then((urlResult) => {
        this.foregroundThumbnail = urlResult.url;
        // TODO: call urlResult.destroy() at some point
      });
    },
    download() {
      this.expireDownload();
      this.downloadFilename = this.filename;
      this.downloading = true;
      this.editor.toUrl().then((urlResult) => {
        this.downloadUrlResult = urlResult;

        if (urlResult.width * urlResult.height > 250000) {
          window.track("Images", "download_from_editor_hd", "Download from Editor HD");
        } else {
          window.track("Images", "download_from_editor_sd", "Download from Editor SD");
        }
      });
    },

    async downloadHD() {
      let needsConfirmation = true;

      const decodedData = await this.pollState();
      if (decodedData?.pl?.full?.full_ordered) {
        needsConfirmation = false;
      }

      if (needsConfirmation) {
        if (!window.confirm(I18n.t("ai_brush.download_hd_confirm"))) {
          return;
        }
      }

      this.expireDownload();
      this.downloadFilename = this.filename.replace("-preview", "");
      this.processAndDownloadHD();
    },

    expireDownload() {
      if (this.downloadUrlResult != null) {
        this.downloadUrlResult.destroy();
        this.downloadUrlResult = null;
      }
    },
    setZoom(newValue) {
      if (newValue > this.zoomMax) {
        this.zoom = this.zoomMax;
      } else if (newValue < this.zoomMin) {
        this.zoom = this.zoomMin;
      } else {
        this.zoom = newValue;
      }
    },
    zoomIn() {
      this.setZoom(this.zoom + 0.2);
      window.track("Editor", "zoom_in", "Zoom in");
    },
    zoomOut() {
      this.setZoom(this.zoom - 0.2);
      window.track("Editor", "zoom_out", "Zoom out");
    },
    zoomReset() {
      this.setZoom(1.0);
      window.track("Editor", "zoom_reset", "Zoom reset");
    },
    zoomFit() {
      var canvas = this.$refs.editorCanvas;
      var innerHeight = canvas.scrollHeight;
      var outerHeight = canvas.clientHeight;
      var innerWidth = canvas.scrollWidth;
      var outerWidth = canvas.clientWidth;
      var zoomHeight = outerHeight / innerHeight;
      var zoomWidth = outerWidth / innerWidth;
      var zoom = zoomWidth < zoomHeight ? zoomWidth : zoomHeight;
      if (zoom > 1.0) zoom = 1.0;
      this.setZoom(zoom);
    },
    updateCursorPosition(e) {
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches ? e.touches[0].clientY : e.clientY;

      var rect = this.$refs.editorArea.getBoundingClientRect();
      this.cursorPosition = [clientX - rect.left, clientY - rect.top];
    },
    touchStarted(e) {
      if (this.canvas && this.tool == "erase_restore") {
        if (e.touches.length >= 2) {
          e.preventDefault();
          this.canvas.isDrawingMode = false;
          return;
        } else {
          this.canvas.isDrawingMode = true; // reset to true for one finger
        }
      }

      if (this.tool == "erase_restore") {
        this.isTouchDown = true;
        this.updateCursorPosition(e);
      }
      window.addEventListener("touchend", this.touchEnded);
    },
    onTouchMove(e) {
      if (e.touches.length == 2) {
        e.preventDefault();
        if (this.tool == "erase_restore") {
          this.editor.cancelPath();
          if (this.detectObjectsEnabled) {
            return; // no pinch to zoom for ai brushes
          }
        }

        var pos = [
          [e.touches[0].clientX, e.touches[0].clientY],
          [e.touches[1].clientX, e.touches[1].clientY],
        ];
        if (this.pinchPositions) {
          var diffx1 = this.pinchPositions[0][0] - this.pinchPositions[1][0];
          var diffy1 = this.pinchPositions[0][1] - this.pinchPositions[1][1];
          var dist1 = Math.sqrt(Math.pow(diffx1, 2) + Math.pow(diffy1, 2));

          var diffx2 = pos[0][0] - pos[1][0];
          var diffy2 = pos[0][1] - pos[1][1];
          var dist2 = Math.sqrt(Math.pow(diffx2, 2) + Math.pow(diffy2, 2));

          this.setZoom(this.zoom + (this.zoom * (dist2 - dist1)) / document.documentElement.clientWidth);
        }
        this.pinchPositions = pos;
      } else if (e.touches.length == 1) {
        if (this.tool == "erase_restore") {
          e.preventDefault();
          this.updateCursorPosition(e);
        }
      }
    },
    touchEnded() {
      this.isTouchDown = false;
      this.pinchPositions = null;
      if (this.canvas && this.tool == "erase_restore") {
        this.canvas.isDrawingMode = true;
      }
      window.removeEventListener("touchend", this.touchEnded);
    },
    iOSpreventGlobalTouchMove(e) {
      if (isiOSSafari) {
        var el = e.target;
        while (el && el !== this.$el) {
          var val = el.getAttribute("data-ios-emulate-scroll");
          if (val === "x") {
            this.iosScrollingX = el;
          } else if (val === "y") {
            this.iosScrollingY = el;
          } else if (val === "xy") {
            this.iosScrollingX = el;
            this.iosScrollingY = el;
          }
          if (this.iosScrollingX != null && this.iosScrollingY != null) break;
          el = el.parentNode;
        }

        if (this.iosScrollingX) {
          var x = e.touches[0].pageX;
          if (this.iosScrollingXPrevious != null) {
            var diffX = x - this.iosScrollingXPrevious;
            this.iosScrollingX.scrollLeft -= diffX;
          }
          this.iosScrollingXPrevious = x;
        }
        if (this.iosScrollingY) {
          var y = e.touches[0].pageY;
          if (this.iosScrollingYPrevious != null) {
            var diffY = y - this.iosScrollingYPrevious;
            this.iosScrollingY.scrollTop -= diffY;
          }
          this.iosScrollingYPrevious = y;
        }

        e.preventDefault();
        return false;
      }
    },
    iOSpreventGlobalTouchMoveEnd() {
      if (isiOSSafari) {
        if (this.iosScrollingX) {
          this.iosScrollingXPrevious = null;
          this.iosScrollingX = null;
        }
        if (this.iosScrollingY) {
          this.iosScrollingYPrevious = null;
          this.iosScrollingY = null;
        }
      }
    },
    setPixabayImages(pixabayImages) {
      this.pixabayImages = pixabayImages;
    },
    openSearchBackgroundOverlay(pixabayImages) {
      this.pixabayImages = pixabayImages;
      this.searchBackgroundOverlayOpen = true;
    },
    updateCursorPosition(e) {
      var clientX = e.touches ? e.touches[0].clientX : e.clientX;
      var clientY = e.touches ? e.touches[0].clientY : e.clientY;

      var rect = this.$refs.editorArea.getBoundingClientRect();
      this.cursorPosition = [clientX - rect.left, clientY - rect.top];
    },
    resizeMask(width, height, options) {
      if (this.detectObjectsAvailable) {
        this.canvas.setDimensions({ width: width || this.$refs.editorCanvas.clientWidth, height: height || this.$refs.editorCanvas.clientHeight }, options);
        this.canvas.renderAll();
      }
    },
    resetCanvas() {
      this.canvas.clear();
    },
    generateMask() {
      clearTimeout(this.maskTimeout);

      this.maskTimeout = setTimeout(() => {
        this.aiBrushProcessing = true;
        this.aiBrushFullProcessing = false;
        var mask = this.$refs.mask;
        mask.toBlob(async (blob) => {
          this.maskBlob = blob;
          this.process();
        }, "image/png");
      }, 500);
    },
    process() {
      this.requestToken({});
    },
    requestToken(upload, tryCount = 1) {
      $.ajax({
        type: "POST",
        url: this.t.trust_tokens_url,
        data: upload.trustTokenData,
        success: (data) => {
          var useToken = (token) => {
            upload.token = token;
            this.processWithToken(upload);
          };
          if (data.request && typeof data.request == "string") {
            eval(data.request);
          }
        },
        error: (xhr) => {
          var submitChallenge = (upload, value) => {
            upload.trustTokenData = value;
            this.requestToken(upload);
          };

          var request_hcaptcha_checkbox = (config, upload, data) => {
            this.captcha_required = true;
            this.captchaConfig = {
              type: "hcaptcha_checkbox",
              config: config,
            };
            this.creditBalance = data.credit_balance;
            this.onCaptchaCompleted = (captcha) => {
              this.captcha_required = false;
              submitChallenge(upload, {
                proof: "hcaptcha_checkbox",
                hcaptcha_response: captcha.value,
              });
            };
          };

          var request_hcaptcha_invisible = (config, upload, data) => {
            hcaptcha_invisible(config)
              .then((captcha) => {
                submitChallenge(upload, {
                  proof: "hcaptcha_invisible",
                  hcaptcha_response: captcha,
                });
              })
              .catch((e) => {
                this.status = "error";
                alert(e.message);
                this.processWithToken(upload);
              });
          };

          if (xhr.status == 422 && xhr.responseJSON && xhr.responseJSON.request && typeof xhr.responseJSON.request == "string") {
            eval(xhr.responseJSON.request);
          } else if (xhr.status == 422 && xhr.responseJSON && xhr.responseJSON.status == "invalid_csrf_token" && tryCount < 3) {
            $("meta[name='csrf-token']").attr("content", xhr.responseJSON.csrf_token);
            this.requestToken(captcha, tryCount + 1);
          } else {
            this.status = "error";
            this.errorMessage = `Error: Failed to prepare upload (error code ${xhr.status}).\nSorry – please reload the page and try again.\nIf the problem persists, contact team@remove.bg`;
            if (window.error_tracking) {
              window.error_tracking.sendError(new Error(`Upload token request failed (try ${tryCount}): Status ${xhr.status}, Text ${(xhr.responseText ? xhr.responseText : "").substring(0, 100)}`));
            }
          }
        },
      });
    },
    processWithToken(upload) {
      if (this.maskBlob == null) {
        return;
      }

      if (this.loading) {
        this.loadingQueued = true;
        return;
      }
      this.loading = true;

      var data = new FormData();
      data.append("image_url", this.originalUrl);

      const maskMode = this.brushMode == "restore" ? "positive_mask" : "negative_mask";
      data.append("mask_mode", maskMode);
      data.append("mask", this.maskBlob);
      this.aiBrushFormData = data;

      // Do not cache size, captcha and previous alpha
      if (!this.resultAlphaBlob) {
        data.append("previous_alpha_url", this.foregroundUrl); // on first iteration submit the foreground url as previous alpha
      } else {
        data.append("previous_alpha", this.resultAlphaBlob);
      }
      data.append("size", "preview");
      data.append("trust_token", upload.token);

      let url = `/images/${this.resultId}/ai_brush`;

      var xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      xhr.responseType = "arraybuffer";
      xhr.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]').getAttribute("content"));

      xhr.onload = (event) => {
        if (xhr.status === 200) {
          this.pollAiBrushResult();
        } else {
          alert(I18n.t(`ai_brush.error.unknown_error`));
        }
      };

      xhr.send(data);
    },

    async processAndDownloadHD() {
      const data = this.aiBrushFormData;
      if (data) {
        data.append("size", "full");
        const url = `/images/${this.resultId}/full_image/ai_brush`;
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", url, true);
        xhr.responseType = "arraybuffer";
        xhr.setRequestHeader("X-CSRF-Token", document.querySelector('meta[name="csrf-token"]').getAttribute("content"));

        xhr.onload = (event) => {
          if (xhr.status === 200) {
            return this.pollAiBrushHDResult();
          } else if (xhr.status === 402) {
            alert(I18n.t(`ai_brush.error.insufficient_credits`));
            this.aiBrushProcessing = false;
            this.aiBrushFullProcessing = false;
          } else {
            alert(I18n.t(`ai_brush.error.unknown_error`));
            this.aiBrushProcessing = false;
            this.aiBrushFullProcessing = false;
          }
        };

        xhr.onerror = (event) => {
          alert(I18n.t(`ai_brush.error.unknown_error`));
          this.aiBrushProcessing = false;
          this.aiBrushFullProcessing = false;
        };

        this.aiBrushProcessing = false;
        this.aiBrushFullProcessing = true;

        xhr.send(data);
      } else {
        // no ai brush was processed yet, just get the hd image and download from editor
        // hiding the button right now
      }
    },
    async pollAiBrushResult() {
      //  Return early if we are already finished
      if (!this.aiBrushProcessing) return;

      const self = this;

      self.aiBrushTimeout = setTimeout(async () => {
        const decodedData = await this.pollState();

        self.aiBrushRemote = decodedData;
        self.aiBrushNextPollIn = decodedData.ai_brush_fetch_in;
        self.hdAvailable = decodedData?.pl?.full?.url;

        if (decodedData?.al?.result) {
          self.loading = false;

          if (decodedData.al.result.error_code) {
            alert(I18n.t(`ai_brush.error.${decodedData.al.result.error_message}`));
            self.aiBrushProcessing = false;
            self.resetCanvas();
          } else {
            ZipCanvas.fromUrl(decodedData.al.result.url, { mimeType: "image/png" }, (progress) => {
              // Nothing to do here
            }).then(async (zipCanvas) => {
              self.resultAlphaBlob = zipCanvas.alphaBlob;

              const data = self.aiBrushFormData;
              data.delete("previous_alpha_url");
              data.set("previous_alpha", zipCanvas.alphaBlob);
              self.aiBrushFormData = data;

              self.change({ foreground: { type: "image", image: zipCanvas.blob } });
              self.resetCanvas();
              self.aiBrushProcessing = false;
            });
          }
        } else {
          self.pollAiBrushResult();
        }
      }, self.aiBrushNextPollIn);
    },
    async pollAiBrushHDResult() {
      if (!this.aiBrushFullProcessing) return;

      const self = this;
      setTimeout(async () => {
        const decodedData = await this.pollState();
        // console.log("HD poll result", decodedData);

        self.aiBrushRemote = decodedData;
        self.aiBrushFullNextPollIn = decodedData.ai_brush_fetch_full_in;

        if (decodedData.fl?.result) {
          if (decodedData.fl.result.error_code) {
            alert(I18n.t(`ai_brush.error.unknown_error`));
            self.aiBrushFullProcessing = false;
            self.aiBrushProcessing = false;
          } else {
            ZipCanvas.fromUrl(decodedData.fl.result.url, { mimeType: "image/png" }, (progress) => {
              // Nothing to do here
            }).then(async (zipCanvas) => {
              self.aiBrushFullProcessing = false;

              let alphaBlob = null;
              let blob = null;
              if (zipCanvas.alphaBlob) {
                alphaBlob = zipCanvas.alphaBlob;
                blob = zipCanvas.blob;
              } else {
                alphaBlob = await (await fetch(zipCanvas.alphaDataUrl)).blob();
                blob = await (await fetch(zipCanvas.dataUrl)).blob();
              }

              // get url from editor with HD blob
              this.downloading = true;
              const oldForeground = this.editor.foregroundBlob;
              this.editor.toUrl({ blob: blob }).then((urlResult) => {
                this.downloadUrlResult = urlResult;
                this.editor.foregroundBlob = oldForeground;

                this.editor.removeSvg();
                this.renderEditor(() => {
                  if (this.detectObjectsAvailable) {
                    this.createMagicCanvas();
                  }

                  this.resizeRestoreBackground();

                  if (urlResult.setBackground) urlResult.setBackground();

                  self.aiBrushFullProcessing = false;
                });

                if (urlResult.width * urlResult.height > 250000) {
                  window.track("Images", "download_from_editor_hd", "Download from Editor HD");
                } else {
                  window.track("Images", "download_from_editor_sd", "Download from Editor SD");
                }
              });

              return Promise.resolve();
            });
          }
        } else {
          // console.log("Ai Brush HD processing not finished, polling again in ", self.aiBrushFullNextPollIn);
          self.pollAiBrushHDResult();
        }
      }, self.aiBrushFullNextPollIn);
    },
    pollState() {
      const self = this;
      return new Promise(function (resolve, reject) {
        const url = `/images/${self.resultId}`;
        const xhr = new XMLHttpRequest();

        xhr.open("GET", url, true);
        xhr.onload = () => {
          const decodedData = self.formatRemote(JSON.parse(xhr.response));
          resolve(decodedData);
        };
        xhr.onerror = () => {
          const decodedData = self.formatRemote(JSON.parse(xhr.response));
          reject(decodedData);
        };
        xhr.send();
      });
    },
    blobToDataUrl(blob) {
      // console.log("blobToDataUrl", blob);

      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve(e.target.result);
        };
        reader.onerror = (e) => {
          reject(e);
        };
        reader.readAsDataURL(blob);
      });
    },
    formatRemote(remote) {
      const pl = remote.pl;
      if (pl) {
        delete remote.pl;
        const plParsed = JSON.parse(Base64.decode(pl));
        remote = { ...remote, pl: plParsed };
      }

      const al = remote.al;
      if (al) {
        delete remote.al;
        const alParsed = JSON.parse(Base64.decode(al));
        remote = { ...remote, al: alParsed };
      }

      const fl = remote.fl;
      if (fl) {
        delete remote.fl;
        const flParsed = JSON.parse(Base64.decode(fl));
        remote = { ...remote, fl: flParsed };
      }

      return remote;
    },
    createMagicCanvas() {
      this.canvas = new fabric.Canvas("mask", { freeDrawingCursor: "none", selection: false });

      this.canvas.freeDrawingBrush.width = this.brushSize;
      this.canvas.freeDrawingBrush.color = this.brushMode === "erase" ? this.aiBrushEraseColor : this.aiBrushRestoreColor;
      this.canvas.freeDrawingBrush.limitedToCanvasSize = true;

      if (this.controlTab == "erase_restore") {
        this.canvas.isDrawingMode = true;
      }

      this.canvas.on("object:added", () => {
        this.undoRedoVisible = false; // no undo or redo when using ai brushes
        this.generateMask();
      });

      this.canvas.on("mouse:over", function (e) {
        if (e.target) e.target.hoverCursor = "hand";
      });

      let that = this;
      this.canvas.on("mouse:down", function (e) {
        clearTimeout(that.maskTimeout);
        that.maskTimeout = null;
      });

      this.resizeMask(this.editor.width, this.editor.height);
    },
    resizeRestoreBackground(zoom = 1) {
      this.$refs.restoreBackground.width = this.editor.width * zoom;
      this.$refs.restoreBackground.height = this.editor.height * zoom;
    },
    showMagicCanvas() {
      document.getElementsByClassName("canvas-container")[0].style.display = "block";
    },
    hideMagicCanvas() {
      document.getElementsByClassName("canvas-container")[0].style.display = "none";
    },
  },
  unmounted: function () {
    // remove event listener
    this.touchEnded();
  },
  components: { Toggle, Slider, OffsetSlider, BlurOptions, ImageList, ColorList, DownloadOverlay, SearchBackgroundOverlay, ToggleButton, MagicOverlay, Button, Dropdown, DropdownBox, NavLink, CheckboxCaptcha },
};
</script>

<style scoped>
#explainer {
  background-color: #555;
}

#explainer a {
  color: #fff !important;
}

@media (min-width: 768px) {
  #explainer {
    background-color: #fff;
  }
  #explainer a {
    color: #454545 !important;
  }
}

.bg-button-active {
  background-color: #555;
}

.bg-button-inactive {
  /* background-color: #e9ebec; */
}

@media (min-width: 768px) {
  .bg-button-active {
    background-color: white;
  }

  .bg-button-inactive {
    background-color: #e9ebec;
  }
}
.editor-layout {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
}
.editor-area {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 35px;
}
.editor-layout-has-control-area-opened .editor-area {
  bottom: 320px;
}

.pointer-events-none {
  pointer-events: none;
}

@media (min-width: 768px) {
  .editor-area {
    right: 400px;
    bottom: 0;
  }
  .editor-layout-has-control-area-opened .editor-area {
    bottom: 0;
  }
}
.editor-area-content {
  position: absolute;
  top: 40px;
  bottom: 0;
  left: 0;
  right: 0;
  display: -webkit-flex;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
.editor-area-content.tool-active {
  cursor: none;
}
@media (min-width: 768px) {
  .editor-area-content {
    bottom: 60px;
    padding: 15px 15px 5px 15px;
  }
}
.editor-area-content-inner {
  overflow: auto;
  border: 1px solid #cbcbcb;
  background-size: 25px;
  max-width: 100%;
  max-height: 100%;
  font-size: 0;
}
.editor-area-content-inner-background-image {
  background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHZpZXdCb3g9JzAgMCA3LjkzNyA3LjkzOCcgaGVpZ2h0PSczMCcgd2lkdGg9JzMwJz48ZyBmaWxsPScjZWVlZmYwJz48cGF0aCBwYWludC1vcmRlcj0nc3Ryb2tlIGZpbGwgbWFya2VycycgZD0nTS4wMTQuMDE0SDMuOTdWMy45N0guMDE0ek0zLjk3IDMuOTY4aDMuOTU0djMuOTU1SDMuOTd6Jy8+PC9nPjwvc3ZnPgo=");
}
.editor-cursor {
  position: absolute;
  pointer-events: none;
  border: 1px solid rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  opacity: 0;
  transition: opacity 700ms;
}

.editor-cursor-erase {
  background-color: #ff4757;
}

.editor-cursor-restore {
  background-color: #489d4a;
}

@media (hover:hover) { /* only for devides supporting hover (as in not ios safari) */
  .editor-area-content.tool-active:hover .editor-cursor {
    /* opacity: 1; */
    opacity: 0.5;
    transition: opacity 0s;
  }
}

.editor-cursor.touchdown {
    /* opacity: 1; */
    opacity: 0.5;
    transition: opacity 0s;
}

.editor-finger-cursor {
  color: #fc1b44;
  position: absolute;
  left: 50%;
  font-size: 30px;
  width: 30px;
  height: 30px;
  margin-left: -15px;
  margin-top: -15px;
  line-height: 1;
}
.editor-upper-controls {
  display: -webkit-flex;
  display: flex;
  justify-content: space-between;
}
@media (min-width: 768px) {
  .editor-upper-controls {
    padding: 10px 7px;
  }
}
.editor-button-group {
  display: -webkit-flex;
  display: flex;
  justify-content: space-between;
  margin: 0 5px;
}
.editor-button {
  background: transparent;
  border: 0;
  line-height: 1;
  color: #585858;
  display: inline-block;
  vertical-align: middle;
  margin: 0 3px;
  border-radius: 2px;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  padding: 0;
}
.editor-button[disabled] {
  opacity: 0.5;
}
.close-button {
  z-index: 18000;
}
.close-button,
.download-button {
  width: 40px;
  height: 40px;
  font-size: 20px;
}
.undo-button,
.redo-button {
  width: 40px;
  height: 40px;
  font-size: 20px;
}
.zoom-in-button,
.zoom-out-button {
  width: 35px;
  height: 35px;
  font-size: 18px;
}
.zoom-restore-button {
  width: 50px;
  height: 30px;
  font-size: 13px;
  font-weight: 400;
  padding-top: 4px;
}
@media (min-width: 768px) {
  .close-button,
  .download-button {
    width: 30px;
    height: 30px;
    font-size: 18px;
  }
  .close-button {
    color: #fff;
    background: #585858;
  }
  .undo-button,
  .redo-button,
  .zoom-in-button,
  .zoom-out-button {
    width: 30px;
    height: 30px;
  }
  .undo-button,
  .redo-button,
  .zoom-in-button,
  .zoom-out-button,
  .zoom-restore-button {
    -webkit-box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.25);
    -moz-box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.25);
    box-shadow: 0px 0px 3px 0px rgba(0, 0, 0, 0.25);
  }
}
.editor-lower-controls {
  text-align: center;
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px;
  height: 60px;
}
.editor-lower-controls .btn {
  margin-bottom: 0;
}
.control-area {
  position: absolute;
  top: auto;
  bottom: 0;
  left: 0;
  right: 0;
  height: 35px;
  display: -webkit-flex;
  display: flex;
  flex-direction: column;
}
.editor-layout-has-control-area-opened .control-area {
  height: 320px;
}
@media (min-width: 768px) {
  .control-area {
    top: 0;
    bottom: 0;
    left: auto;
    right: 0;
    width: 400px;
    height: auto;
  }
  .editor-layout-has-control-area-opened .control-area {
    height: auto;
  }
}
.control-tab-buttons {
  display: -webkit-flex;
  display: flex;
  align-items: stretch;
  min-height: 35px;
}
@media (min-width: 768px) {
  .control-tab-buttons {
    min-height: 45px;
  }
}
.control-tabs {
  flex: 1 1 auto;
  display: -webkit-flex;
  display: flex;
  max-height: 100%;
  overflow: auto;
  background: #2b2b2b;
  color: #fff;
}
.control-tab {
  flex: 1 1 auto;
  width: 100%;
  padding: 10px;
}
@media (min-width: 768px) {
  .control-tabs {
    background: #f4f4f4;
    color: #585858;
  }
}
.tab-button {
  flex: 1 1 auto;
  border: 0;
  color: #585858;
  background: #f4f4f4;
  font-size: 80%;
  font-weight: 400;
  line-height: 1;
  padding: 0 5px;
  border-radius: 0;
  outline: none;
  text-transform: uppercase;
}
.tab-button-active {
  color: #fff;
  background: #2b2b2b;
}
@media (min-width: 768px) {
  .tab-button {
    color: #585858;
    background: #fff;
    font-size: 100%;
    font-weight: 700;
    min-height: 45px;
    line-height: 1.3;
    padding-top: 3px;
  }
  .tab-button i {
    font-size: 110%;
    display: inline-block;
    margin-right: 5px;
  }
  .tab-button-active {
    color: #585858;
    background: #f4f4f4;
  }
}
.overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  z-index: 15500;
  background: rgba(255, 255, 255, 0.8);
  display: -webkit-flex;
  display: flex;
  align-items: center;
  justify-content: center;
}
@media (min-width: 768px) {
  .background-tab-content {
    margin-left: 15px;
  }
}
.background-type-toggle {
  display: none;
}
@media (min-width: 768px) {
  .background-type-toggle {
    display: block;
  }
  .background-type-tab {
    display: none;
  }
  .background-type-tab-active {
    display: block;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
